import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  listStyleTests, createStyleTest, deleteStyleTest,
  uploadStyleTestRefImage, deleteStyleTestRefImage, importStyleTestRefURL,
  generateStyleTestImage, applyStyleTest,
  listGenerationTemplatesForProject, getImageTaskStatus, getAssetSearchTasks,
  getScriptAnalysis, isApiError,
} from '../api';
import { AssetStyleTest as StyleTestType, GenerationTemplate, AssetSearchTask } from '../types';
import { useToast } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import { tosAccelerate } from '../utils/tosAccelerate';
import { MAX_TOTAL_REF_IMAGES } from './project-detail/utils/editDefaults';
import { useImageUploadZone } from '../hooks/useImageUploadZone';
import { useImageAspectRatioGuard } from '../hooks/useImageAspectRatioGuard';
import { getDropHighlightStyle } from '../utils/imageFiles';
import {
  BaseMetadataField,
  normalizeAssetName,
  resolveBaseMetadata,
} from './project-detail/utils/baseMetadata';

const ELEMENT_LABEL_KEYS: Record<string, string> = {
  character: 'styleTest.elementCharacter',
  scene: 'styleTest.elementScene',
  prop: 'styleTest.elementProp',
};

export default function AssetStyleTestPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const elementType = searchParams.get('elementType') || '';
  const elementName = searchParams.get('elementName') || '';
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation('common');

  const [tests, setTests] = useState<StyleTestType[]>([]);
  const [templates, setTemplates] = useState<GenerationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [baseConflictFields, setBaseConflictFields] = useState<BaseMetadataField[]>([]);
  const [serverBaseConflictFields, setServerBaseConflictFields] = useState<BaseMetadataField[]>([]);
  const [baseMetadataStatus, setBaseMetadataStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [verifiedAuthorityKey, setVerifiedAuthorityKey] = useState('');
  const authorityRequestSeqRef = useRef(0);
  const acceptedAuthorityEpochRef = useRef(0);
  const testsRequestSeqRef = useRef(0);
  const currentAuthorityKey = `${elementType}\u001f${elementName}`;
  const currentAuthorityKeyRef = useRef(currentAuthorityKey);
  currentAuthorityKeyRef.current = currentAuthorityKey;
  const authorityReady = baseMetadataStatus === 'ready' && verifiedAuthorityKey === currentAuthorityKey;
  const effectiveBaseConflictFields = Array.from(new Set([
    ...baseConflictFields,
    ...serverBaseConflictFields,
  ]));
  const applyBlocked = !authorityReady ||
    effectiveBaseConflictFields.includes('baseRefImages') ||
    effectiveBaseConflictFields.includes('baseImageUrls');
  const applyBlockedMessage = authorityReady
    ? t('styleTest.baseMetadataConflict')
    : t('styleTest.baseMetadataUnavailable');

  // Search images for import
  const [searchTasks, setSearchTasks] = useState<AssetSearchTask[]>([]);
  const [searchImagesLoaded, setSearchImagesLoaded] = useState(false);

  // Search select modal (same pattern as ProjectDetail)
  const [searchSelectModal, setSearchSelectModal] = useState<{
    testId: string;
    currentRefCount: number;
    maxTotal: number;
    images: string[];
    groups: { keywords: string; state?: string; images: string[] }[];
  } | null>(null);
  const [searchSelectedUrls, setSearchSelectedUrls] = useState<string[]>([]);
  const [searchLightboxIndex, setSearchLightboxIndex] = useState<number | null>(null);
  const [savingSearchSelection, setSavingSearchSelection] = useState(false);

  // Apply confirm
  const [applyConfirm, setApplyConfirm] = useState<{ testId: string; imageUrl: string; templateName: string } | null>(null);

  const fetchTests = useCallback(async () => {
    if (!projectId) return;
    const requestSeq = testsRequestSeqRef.current + 1;
    testsRequestSeqRef.current = requestSeq;
    try {
      const data = await listStyleTests(projectId, elementType, elementName);
      if (testsRequestSeqRef.current === requestSeq) setTests(data || []);
    } catch {}
  }, [projectId, elementType, elementName]);

  useEffect(() => {
    if (!projectId) {
      setBaseMetadataStatus('error');
      setLoading(false);
      return;
    }
    const requestSeq = authorityRequestSeqRef.current + 1;
    authorityRequestSeqRef.current = requestSeq;
    let cancelled = false;
    const init = async () => {
      setLoading(true);
      setBaseMetadataStatus('loading');
      setVerifiedAuthorityKey('');
      setBaseConflictFields([]);
      setServerBaseConflictFields([]);
      setApplyConfirm(null);
      try {
        const [tpls, scriptAnalysis] = await Promise.all([
          listGenerationTemplatesForProject().catch(() => []),
          getScriptAnalysis(projectId),
          fetchTests(),
        ]);
        if (cancelled || authorityRequestSeqRef.current !== requestSeq) return;
        setTemplates(tpls);
        const serialized = elementType === 'character'
          ? scriptAnalysis.characters
          : elementType === 'scene'
            ? scriptAnalysis.scenes
            : elementType === 'prop'
              ? scriptAnalysis.props
              : null;
        if (serialized === null) throw new Error('unsupported element type');
        const parsed: unknown = JSON.parse(serialized || '[]');
        if (!Array.isArray(parsed)) throw new Error('invalid analysis assets');
        const sources = parsed as Array<{ name: string; baseRefImages?: string[]; baseImageUrls?: string[]; selectedBaseImageUrl?: string }>;
        const targetName = normalizeAssetName(elementName);
        const targets = sources.filter(source => normalizeAssetName(source.name || '') === targetName);
        if (!targetName || targets.length === 0) throw new Error('analysis target missing');
        const metadata = resolveBaseMetadata(targets);
        setBaseConflictFields(metadata.conflictFields);
        acceptedAuthorityEpochRef.current = requestSeq;
        setVerifiedAuthorityKey(currentAuthorityKey);
        setBaseMetadataStatus('ready');
      } catch {
        if (!cancelled && authorityRequestSeqRef.current === requestSeq) setBaseMetadataStatus('error');
      } finally {
        if (!cancelled && authorityRequestSeqRef.current === requestSeq) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [fetchTests, projectId, elementType, elementName, currentAuthorityKey]);

  // Load search images eagerly on init
  const loadSearchImages = useCallback(async () => {
    if (searchImagesLoaded || !projectId) return;
    try {
      const tasks = await getAssetSearchTasks(projectId);
      setSearchTasks(tasks.filter(t => t.status === 'completed' && t.parsedImageUrls && t.parsedImageUrls.length > 0));
      setSearchImagesLoaded(true);
    } catch {}
  }, [projectId, searchImagesLoaded]);

  useEffect(() => { loadSearchImages(); }, [loadSearchImages]);

  const handleAddTemplate = async () => {
    if (!selectedTemplateId || !projectId) return;
    try {
      await createStyleTest(projectId, { elementType, elementName, templateId: selectedTemplateId });
      await fetchTests();
      setSelectedTemplateId('');
      toast.success(t('styleTest.addedPreset'));
    } catch (e: any) {
      toast.error(e.message || t('styleTest.addFailed'));
    }
  };

  const handleDelete = async (testId: string) => {
    if (!projectId) return;
    try {
      await deleteStyleTest(projectId, testId);
      setTests(prev => prev.filter(t => t.id !== testId));
    } catch {}
  };

  const openSearchSelectModal = (testId: string, currentRefCount: number, maxTotal: number) => {
    const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
    const category = categoryMap[elementType] || 0;
    const matched = searchTasks.filter(t =>
      t.category === category && t.assetName === elementName
    );
    const groups = matched
      .sort((a, b) => {
        if (a.assetState !== b.assetState) return a.assetState.localeCompare(b.assetState);
        return a.keywordIndex - b.keywordIndex;
      })
      .map(t => ({ keywords: t.keywords, state: t.assetState || '', images: t.parsedImageUrls || [] }))
      .filter(g => g.images.length > 0);
    const images = groups.flatMap(g => g.images);
    if (images.length === 0) {
      toast.warning(t('styleTest.noSearchResults'));
      return;
    }
    setSearchSelectModal({ testId, currentRefCount, maxTotal, images, groups });
    setSearchSelectedUrls([]);
    setSearchLightboxIndex(null);
  };

  const handleConfirmSearchSelect = async () => {
    if (!projectId || !searchSelectModal || searchSelectedUrls.length === 0) return;
    setSavingSearchSelection(true);
    try {
      await importStyleTestRefURL(projectId, searchSelectModal.testId, searchSelectedUrls);
      toast.success(t('styleTest.importedRefImages', { count: searchSelectedUrls.length }));
      setSearchSelectModal(null);
      await fetchTests();
    } catch (e: any) {
      toast.error(e.message || t('styleTest.importFailed'));
    } finally {
      setSavingSearchSelection(false);
    }
  };

  const searchImageCount = (() => {
    const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
    const category = categoryMap[elementType] || 0;
    return searchTasks
      .filter(t => t.category === category && t.assetName === elementName)
      .reduce((sum, t) => sum + (t.parsedImageUrls?.length || 0), 0);
  })();

  const handleApply = async () => {
    if (!applyConfirm || !projectId) return;
    if (applyBlocked) {
      toast.error(applyBlockedMessage);
      setApplyConfirm(null);
      return;
    }
    const applyAuthorityKey = currentAuthorityKey;
    const applyAuthorityEpoch = acceptedAuthorityEpochRef.current;
    const isCurrentApplyAuthority = () =>
      currentAuthorityKeyRef.current === applyAuthorityKey &&
      authorityRequestSeqRef.current === applyAuthorityEpoch &&
      acceptedAuthorityEpochRef.current === applyAuthorityEpoch;
    try {
      await applyStyleTest(projectId, applyConfirm.testId, applyConfirm.imageUrl);
      if (!isCurrentApplyAuthority()) return;
      toast.success(t('styleTest.appliedToProject'));
      setApplyConfirm(null);
    } catch (error: unknown) {
      if (!isCurrentApplyAuthority()) return;
      const fields = isApiError(error) && error.status === 409 && error.code === 'base_metadata_conflict'
        ? (error.fields ?? []).filter(
          (field): field is BaseMetadataField => field === 'baseRefImages' || field === 'baseImageUrls',
        )
        : [];
      if (fields.length > 0) {
        setServerBaseConflictFields(current => Array.from(new Set([...current, ...fields])));
        toast.error(t('styleTest.baseMetadataConflict'));
      } else {
        toast.error(t('styleTest.applyFailed'));
      }
      setApplyConfirm(null);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 'var(--container-max)', margin: '2rem auto', padding: '0 1rem' }}>
        <p style={{ color: 'var(--text-3)' }}>{t('styleTest.loading')}</p>
      </div>
    );
  }

  const usedTemplateIds = new Set(tests.map(t => t.templateId));
  const elementLabel = t(ELEMENT_LABEL_KEYS[elementType] || elementType);

  return (
    <div style={{ maxWidth: 'var(--container-max)', margin: '0 auto', padding: '1.5rem 1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button
          className="btn btn-secondary"
          style={{ fontSize: '0.85rem' }}
          onClick={() => navigate(`/projects/${projectId}`)}
        >
          {t('styleTest.back')}
        </button>
        <h1 style={{ margin: 0, fontSize: '1.25rem' }}>
          {t('styleTest.pageTitle', { element: elementLabel, name: elementName })}
        </h1>
      </div>

      {/* Add template selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.85rem', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{t('styleTest.addPresetLabel')}</span>
        <select
          value={selectedTemplateId}
          onChange={e => setSelectedTemplateId(e.target.value)}
          className="input"
          style={{ flex: 1, minWidth: '200px', maxWidth: '400px', fontSize: '0.85rem' }}
        >
          <option value="">{t('styleTest.selectPresetPlaceholder')}</option>
          {templates.map(tpl => (
            <option key={tpl.id} value={tpl.id} disabled={usedTemplateIds.has(tpl.id)}>
              {tpl.name}{usedTemplateIds.has(tpl.id) ? ` ${t('styleTest.alreadyAdded')}` : ''}
            </option>
          ))}
        </select>
        <button
          className="btn btn-primary"
          style={{ fontSize: '0.85rem' }}
          disabled={!selectedTemplateId}
          onClick={handleAddTemplate}
        >
          {t('styleTest.add')}
        </button>
      </div>

      {!authorityReady ? (
        <p data-testid="style-test-base-unavailable" style={{ color: 'var(--danger)', fontSize: '0.82rem' }}>
          {t('styleTest.baseMetadataUnavailable')}
        </p>
      ) : applyBlocked && (
        <p data-testid="style-test-base-conflict" style={{ color: 'var(--danger)', fontSize: '0.82rem' }}>
          {t('styleTest.baseMetadataConflict')}
        </p>
      )}

      {/* Test cards */}
      {tests.length === 0 ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-3)', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--surface)' }}>
          {t('styleTest.emptyHint')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {tests.map(test => (
            <StyleTestCard
              key={test.id}
              test={test}
              projectId={projectId!}
              templates={templates}
              onDelete={() => handleDelete(test.id)}
              onRefresh={fetchTests}
              onApply={(imageUrl) => {
                if (applyBlocked) {
                  toast.error(applyBlockedMessage);
                  return;
                }
                setApplyConfirm({ testId: test.id, imageUrl, templateName: test.templateName });
              }}
              applyDisabled={applyBlocked}
              searchImageCount={searchImageCount}
              onSearchSelect={(currentRefCount, maxTotal) => openSearchSelectModal(test.id, currentRefCount, maxTotal)}
              elementType={elementType}
              elementName={elementName}
            />
          ))}
        </div>
      )}

      {/* Search ref image select modal */}
      {searchSelectModal && (() => {
        const maxSelectable = Math.max(0, searchSelectModal.maxTotal - searchSelectModal.currentRefCount);
        return (
          <Modal
            open={true}
            onClose={() => setSearchSelectModal(null)}
            title={t('styleTest.selectRefTitle', { name: elementName })}
            width="800px"
            footer={
              <>
                <button
                  onClick={() => setSearchSelectedUrls([])}
                  className="btn btn-ghost"
                  style={{ marginRight: 'auto', fontSize: '0.8rem', color: 'var(--text-3)' }}
                  disabled={searchSelectedUrls.length === 0}
                >
                  {t('styleTest.clearSelection')}
                </button>
                <button onClick={() => setSearchSelectModal(null)} className="btn btn-secondary">{t('styleTest.cancel')}</button>
                <button
                  onClick={handleConfirmSearchSelect}
                  disabled={searchSelectedUrls.length === 0 || savingSearchSelection}
                  className="btn btn-primary"
                >
                  {savingSearchSelection ? t('styleTest.savingSelection') : t('styleTest.confirmAddCount', { count: searchSelectedUrls.length })}
                </button>
              </>
            }
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '0.75rem', fontSize: '0.8rem', color: 'var(--text-3)',
            }}>
              <span>
                {t('styleTest.refImageCount', { current: searchSelectModal.currentRefCount, max: searchSelectModal.maxTotal })}
                {maxSelectable > 0 ? t('styleTest.canSelectMore', { count: maxSelectable }) : t('styleTest.reachedLimit')}
              </span>
              <span style={{
                padding: '2px 10px', borderRadius: '10px', fontSize: '0.75rem',
                background: searchSelectedUrls.length > 0 ? 'var(--primary)' : 'var(--bg-2)',
                color: searchSelectedUrls.length > 0 ? 'white' : 'var(--text-3)',
                transition: 'all 0.2s',
              }}>
                {t('styleTest.selectedCount', { current: searchSelectedUrls.length, max: maxSelectable })}
              </span>
            </div>
            <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '4px' }}>
              {searchSelectModal.groups.map((group, gi) => {
                const groupStartIndex = searchSelectModal.groups.slice(0, gi).reduce((sum, g) => sum + g.images.length, 0);
                return (
                  <div key={gi} style={{ marginBottom: gi < searchSelectModal.groups.length - 1 ? '1.25rem' : 0 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      marginBottom: '0.5rem', paddingBottom: '0.35rem',
                      borderBottom: '1px solid var(--border)',
                    }}>
                      <span style={{
                        fontSize: '0.7rem', color: 'white', background: 'var(--primary)',
                        borderRadius: '3px', padding: '1px 6px', fontWeight: 600, flexShrink: 0,
                      }}>
                        {gi + 1}
                      </span>
                      {group.state && (
                        <span style={{
                          fontSize: '0.7rem', color: 'var(--primary)', background: 'var(--primary-light)',
                          borderRadius: '3px', padding: '1px 6px', fontWeight: 500, flexShrink: 0,
                          border: '1px solid var(--primary)',
                        }}>
                          {group.state}
                        </span>
                      )}
                      <span style={{
                        fontSize: '0.8rem', color: 'var(--text-2)', fontStyle: 'italic',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {group.keywords}
                      </span>
                      <span style={{ fontSize: '0.72rem', color: 'var(--text-3)', flexShrink: 0 }}>
                        {t('styleTest.imageCount', { count: group.images.length })}
                      </span>
                    </div>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                      gap: '0.6rem',
                    }}>
                      {group.images.map((url, i) => {
                        const flatIndex = groupStartIndex + i;
                        const isSelected = searchSelectedUrls.includes(url);
                        const maxReached = searchSelectedUrls.length >= maxSelectable;
                        const isDisabled = !isSelected && maxReached;
                        return (
                          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            <div
                              onClick={() => {
                                if (isDisabled) return;
                                setSearchSelectedUrls(prev =>
                                  isSelected ? prev.filter(u => u !== url) : [...prev, url]
                                );
                              }}
                              style={{
                                position: 'relative',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                opacity: isDisabled ? 0.35 : 1,
                                borderRadius: '8px 8px 0 0',
                                overflow: 'hidden',
                                border: isSelected ? '2.5px solid var(--primary)' : '2.5px solid var(--border)',
                                borderBottom: 'none',
                                boxShadow: isSelected ? '0 0 0 1px var(--primary), 0 2px 8px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
                                transition: 'all 0.15s ease',
                                transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                background: 'var(--bg-1)',
                              }}
                            >
                              <img
                                src={tosAccelerate(url)}
                                alt={t('styleTest.searchResultAlt', { index: flatIndex + 1 })}
                                loading="lazy"
                                style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block' }}
                              />
                              {isSelected && (
                                <div style={{
                                  position: 'absolute', top: '6px', right: '6px',
                                  width: '22px', height: '22px', borderRadius: '50%',
                                  background: 'var(--primary)', color: 'white',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '13px', fontWeight: 700,
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                                }}>
                                  {searchSelectedUrls.indexOf(url) + 1}
                                </div>
                              )}
                              {!isSelected && !isDisabled && (
                                <div style={{
                                  position: 'absolute', top: '6px', right: '6px',
                                  width: '22px', height: '22px', borderRadius: '50%',
                                  background: 'rgba(0,0,0,0.3)', border: '2px solid rgba(255,255,255,0.7)',
                                }} />
                              )}
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); setSearchLightboxIndex(flatIndex); }}
                              style={{
                                border: isSelected ? '2.5px solid var(--primary)' : '2.5px solid var(--border)',
                                borderTop: `1px solid ${isSelected ? 'var(--primary)' : 'var(--border)'}`,
                                borderRadius: '0 0 8px 8px',
                                background: 'var(--bg-2)',
                                padding: '4px 0',
                                fontSize: '0.72rem',
                                color: 'var(--text-3)',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                                transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                opacity: isDisabled ? 0.35 : 1,
                              }}
                            >
                              {t('styleTest.showLargeImage')}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            {searchLightboxIndex !== null && ReactDOM.createPortal(
              <SearchLightbox
                urls={searchSelectModal.images}
                index={searchLightboxIndex}
                onClose={() => setSearchLightboxIndex(null)}
                onChange={setSearchLightboxIndex}
                selectedUrls={searchSelectedUrls}
                maxSelectable={maxSelectable}
                onMultiSelect={(url) => {
                  setSearchSelectedUrls(prev =>
                    prev.includes(url) ? prev.filter(u => u !== url) : prev.length < maxSelectable ? [...prev, url] : prev
                  );
                }}
              />,
              document.body
            )}
          </Modal>
        );
      })()}

      {/* Apply confirmation */}
      <ConfirmDialog
        open={!!applyConfirm}
        title={t('styleTest.applyTitle')}
        message={t('styleTest.applyMessage', { name: elementName, templateName: applyConfirm?.templateName })}
        onConfirm={handleApply}
        onCancel={() => setApplyConfirm(null)}
      />
    </div>
  );
}

// ============================================================
// StyleTestCard
// ============================================================

interface StyleTestCardProps {
  test: StyleTestType;
  projectId: string;
  templates: GenerationTemplate[];
  onDelete: () => void;
  onRefresh: () => void;
  onApply: (imageUrl: string) => void;
  applyDisabled?: boolean;
  searchImageCount: number;
  onSearchSelect: (currentRefCount: number, maxTotal: number) => void;
  elementType: string;
  elementName: string;
}

function StyleTestCard({ test, projectId, templates, onDelete, onRefresh, onApply, applyDisabled = false, searchImageCount, onSearchSelect, elementType, elementName }: StyleTestCardProps) {
  const toast = useToast();
  const { t } = useTranslation('common');
  const guardAspectRatio = useImageAspectRatioGuard();
  const [collapsed, setCollapsed] = useState(false);
  const [refImages, setRefImages] = useState<string[]>(test.parsedRefImages || []);
  const [generatedImages, setGeneratedImages] = useState<string[]>(test.parsedGeneratedImages || []);
  const [prompt, setPrompt] = useState(test.prompt || '');
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [hoveredBaseIndex, setHoveredBaseIndex] = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sync local state when parent re-fetches tests
  useEffect(() => {
    setRefImages(test.parsedRefImages || []);
    setGeneratedImages(test.parsedGeneratedImages || []);
    setPrompt(test.prompt || '');
  }, [test.parsedRefImages, test.parsedGeneratedImages, test.prompt]);

  // Poll for prompt generation completion
  const promptPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (test.promptStatus === 'generating' || test.promptStatus === 'pending') {
      promptPollRef.current = setInterval(() => { onRefresh(); }, 3000);
      return () => { if (promptPollRef.current) clearInterval(promptPollRef.current); };
    }
    return () => { if (promptPollRef.current) clearInterval(promptPollRef.current); };
  }, [test.promptStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  const isPromptGenerating = test.promptStatus === 'generating' || test.promptStatus === 'pending';

  // Get template config for this element type
  const tpl = templates.find(t => t.id === test.templateId);
  const fixedPrompt = elementType === 'character' ? (tpl?.charFixedPrompt ?? '')
    : elementType === 'scene' ? (tpl?.sceneFixedPrompt ?? '')
    : (tpl?.propFixedPrompt ?? '');
  const projectRefImagesJSON = elementType === 'character' ? tpl?.charProjectRefImages
    : elementType === 'scene' ? tpl?.sceneProjectRefImages
    : tpl?.propProjectRefImages;
  let templateProjectRefImages: string[] = [];
  try { if (projectRefImagesJSON) templateProjectRefImages = JSON.parse(projectRefImagesJSON); } catch {}
  const maxRefImages = Math.max(0, MAX_TOTAL_REF_IMAGES - templateProjectRefImages.length);

  // Project ref image exclusion
  const projRefExcludeKey = `style-test-proj-ref-excluded:${test.id}`;
  const loadExcluded = () => { try { return JSON.parse(localStorage.getItem(projRefExcludeKey) || '[]') as string[]; } catch { return []; } };
  const [excludedProjectRefUrls, setExcludedProjectRefUrls] = useState<string[]>(loadExcluded);
  const projectRefImages = templateProjectRefImages.filter(u => !excludedProjectRefUrls.includes(u));

  const canGenerate = true;

  // Recover task from localStorage
  const taskKey = `style-test-task-${test.id}`;
  useEffect(() => {
    const saved = localStorage.getItem(taskKey);
    if (saved) {
      setTaskId(saved);
      setGenerating(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll task status
  useEffect(() => {
    if (!taskId) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await getImageTaskStatus(taskId);
        if (res.status === 2) {
          clearInterval(pollRef.current!);
          localStorage.removeItem(taskKey);
          setGenerating(false);
          setTaskId(null);
          setCollapsed(false);
          onRefresh();
        } else if (res.status === 3) {
          clearInterval(pollRef.current!);
          localStorage.removeItem(taskKey);
          setGenerating(false);
          setTaskId(null);
          setGenError(res.error || t('styleTest.generateFailed'));
        }
      } catch {}
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUploadRef = async (file: File) => {
    if (!(await guardAspectRatio(file))) return;
    try {
      const res = await uploadStyleTestRefImage(projectId, test.id, file);
      setRefImages(prev => [...prev, res.url]);
    } catch (e: any) {
      toast.error(e.message || t('styleTest.uploadFailed'));
    }
  };

  const refZone = useImageUploadZone({
    multiple: true,
    maxFiles: Math.max(0, maxRefImages - refImages.length),
    onFiles: async (imgs) => {
      for (const f of imgs) await handleUploadRef(f);
    },
  });

  const handleDeleteRef = async (url: string) => {
    try {
      await deleteStyleTestRefImage(projectId, test.id, url);
      setRefImages(prev => prev.filter(u => u !== url));
    } catch {}
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) { toast.error(t('styleTest.enterPrompt')); return; }
    setGenError(null);
    setGenerating(true);
    try {
      const res = await generateStyleTestImage(projectId, test.id, prompt);
      setTaskId(res.taskId);
      localStorage.setItem(taskKey, res.taskId);
    } catch (e: any) {
      setGenError(e.message || t('styleTest.generateFailed'));
      setGenerating(false);
    }
  };

  const handleReset = () => {
    setPrompt(test.prompt || fixedPrompt);
    setExcludedProjectRefUrls([]);
    localStorage.removeItem(projRefExcludeKey);
  };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden', background: 'var(--surface)' }}>
      {/* Title row */}
      <div
        onClick={() => setCollapsed(v => !v)}
        style={{ padding: '0.4rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', userSelect: 'none', background: 'var(--bg)' }}
      >
        <span style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-2)' }}>{test.templateName}</span>
        {generatedImages.length > 0 && (
          <span style={{ fontSize: '0.7rem', color: 'var(--text-3)' }}>({t('styleTest.imageCount', { count: generatedImages.length })})</span>
        )}
        {generating && <span style={{ fontSize: '0.7rem', color: 'var(--primary)' }}>{t('styleTest.generatingText')}</span>}
        <button
          onClick={e => { e.stopPropagation(); setConfirmDelete(true); }}
          style={{
            marginLeft: 'auto', marginRight: '0.5rem',
            background: 'none', border: 'none', color: 'var(--danger)',
            fontSize: '0.7rem', cursor: 'pointer', padding: 0,
          }}
        >
          {t('styleTest.delete')}
        </button>
        <span style={{ fontSize: '0.65rem', color: 'var(--text-3)', transition: 'transform 0.2s', display: 'inline-block', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}>&#9660;</span>
      </div>

      {!collapsed && (
        <div style={{ padding: '0 0.75rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', paddingTop: '0.6rem' }}>
            {/* Left: ref images */}
            <div style={{ display: 'flex', gap: '0', alignItems: 'flex-start', flexShrink: 0 }}>
              <div>
                <p style={{ margin: '0 0 0.3rem', fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('styleTest.refImages', { current: refImages.length, max: maxRefImages })}</p>
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {refImages.map((url, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img src={tosAccelerate(url)} alt="" style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)', display: 'block' }} />
                      <button onClick={() => handleDeleteRef(url)} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', borderRadius: '50%', background: 'var(--danger)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>×</button>
                    </div>
                  ))}
                  {refImages.length < maxRefImages && (
                    <label {...refZone.zoneProps} style={{ width: '52px', height: '52px', border: '1px dashed var(--border)', borderRadius: '6px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-3)', gap: '2px', ...getDropHighlightStyle(refZone.isOver) }}>
                      <span style={{ fontSize: '1.2rem', lineHeight: 1 }}>+</span>
                      <span style={{ fontSize: '0.55rem' }}>{t('styleTest.upload')}</span>
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadRef(f); e.target.value = ''; }} />
                    </label>
                  )}
                  {searchImageCount > 0 && refImages.length < maxRefImages && (
                    <button
                      onClick={() => onSearchSelect(refImages.length, maxRefImages)}
                      style={{
                        width: '52px', height: '52px', flexShrink: 0,
                        background: 'none', border: '1px dashed var(--primary)',
                        color: 'var(--primary)', borderRadius: '4px',
                        cursor: 'pointer', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', gap: '2px', padding: 0,
                      }}
                      title={t('styleTest.searchFromResults')}
                    >
                      <span style={{ fontSize: '0.9rem', lineHeight: 1 }}>&#128269;</span>
                      <span style={{ fontSize: '0.5rem', lineHeight: 1, textAlign: 'center' }}>{t('styleTest.searchCount', { count: searchImageCount })}</span>
                    </button>
                  )}
                </div>
              </div>
              {projectRefImages.length > 0 && (
                <>
                  <div style={{ width: '1px', background: 'var(--border)', alignSelf: 'stretch', margin: '0 0.5rem', flexShrink: 0 }} />
                  <div>
                    <p style={{ margin: '0 0 0.3rem', fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('styleTest.projectRefImages', { count: projectRefImages.length })}</p>
                    <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      {projectRefImages.map((url, i) => (
                        <div key={i} style={{ position: 'relative' }}>
                          <img src={tosAccelerate(url)} alt="" style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--border)', display: 'block' }} />
                          <button
                            onClick={() => {
                              const next = [...excludedProjectRefUrls, url];
                              setExcludedProjectRefUrls(next);
                              localStorage.setItem(projRefExcludeKey, JSON.stringify(next));
                            }}
                            style={{ position: 'absolute', top: '-6px', right: '-6px', width: '18px', height: '18px', borderRadius: '50%', background: 'var(--danger)', color: 'white', border: 'none', cursor: 'pointer', fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
                          >×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{ width: '1px', background: 'var(--border)', alignSelf: 'stretch', flexShrink: 0 }} />

            {/* Right: prompt + action buttons */}
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('styleTest.promptLabel')}</p>
              {isPromptGenerating ? (
                <div style={{ background: 'var(--primary-light)', borderRadius: '4px', padding: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="loading-spinner" style={{ width: '14px', height: '14px', borderWidth: '2px' }} />
                  <span style={{ fontSize: '0.82rem', color: 'var(--primary)' }}>{t('styleTest.aiGeneratingPrompt')}</span>
                </div>
              ) : test.promptStatus === 'failed' ? (
                <div style={{ background: '#fff0f0', borderRadius: '4px', padding: '0.75rem' }}>
                  <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--danger)' }}>{t('styleTest.promptFailed')}</p>
                  <button
                    style={{ marginTop: '0.4rem', background: 'none', border: '1px solid var(--primary)', color: 'var(--primary)', borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                    onClick={() => setEditingPrompt(true)}
                  >
                    {t('styleTest.manualEdit')}
                  </button>
                </div>
              ) : editingPrompt ? (
                <div>
                  <textarea
                    value={prompt}
                    onChange={e => setPrompt(e.target.value)}
                    style={{
                      width: '100%', minHeight: '80px', fontSize: '0.82rem', border: '1px solid var(--primary)',
                      borderRadius: '4px', padding: '0.5rem', resize: 'vertical', boxSizing: 'border-box',
                      fontFamily: 'inherit', lineHeight: 1.5,
                    }}
                    placeholder={t('styleTest.promptPlaceholder')}
                  />
                  <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem', flexWrap: 'wrap' }}>
                    <button
                      style={{ background: 'none', border: '1px solid var(--primary)', color: 'var(--primary)', borderRadius: '4px', padding: '2px 10px', fontSize: '0.75rem', cursor: 'pointer' }}
                      onClick={() => setEditingPrompt(false)}
                    >
                      {t('styleTest.done')}
                    </button>
                    <button
                      style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-2)', borderRadius: '4px', padding: '2px 10px', fontSize: '0.75rem', cursor: 'pointer' }}
                      onClick={() => { setEditingPrompt(false); setPrompt(test.prompt || ''); }}
                    >
                      {t('styleTest.cancel')}
                    </button>
                    <button
                      style={{ background: 'none', border: '1px solid var(--text-3)', color: 'var(--text-3)', borderRadius: '4px', padding: '2px 10px', fontSize: '0.75rem', cursor: 'pointer' }}
                      onClick={handleReset}
                      title={t('styleTest.resetTitle')}
                    >
                      {t('styleTest.reset')}
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={!canGenerate || generating}
                      style={{
                        background: canGenerate && !generating ? 'var(--success)' : 'var(--success-light)',
                        border: 'none', color: canGenerate && !generating ? 'white' : 'var(--success)',
                        borderRadius: '4px', padding: '2px 10px', fontSize: '0.75rem',
                        cursor: canGenerate && !generating ? 'pointer' : 'not-allowed',
                        display: 'flex', alignItems: 'center', gap: '4px',
                      }}
                    >
                      {generating && <span className="loading-spinner" style={{ width: '10px', height: '10px', borderWidth: '2px' }} />}
                      {generating ? t('styleTest.generatingText') : t('styleTest.generateBaseImage')}
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ background: 'var(--primary-light)', borderRadius: '4px', padding: '0.5rem 0.75rem' }}>
                  {prompt ? (
                    <p style={{ margin: '0 0 0.4rem 0', fontSize: '0.82rem', color: 'var(--primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                      {prompt}
                    </p>
                  ) : (
                    <p style={{ margin: '0 0 0.4rem 0', fontSize: '0.82rem', color: 'var(--text-3)' }}>{t('styleTest.noPrompt')}</p>
                  )}
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                    <button
                      style={{ background: 'none', border: '1px solid var(--primary)', color: 'var(--primary)', borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                      onClick={() => setEditingPrompt(true)}
                    >
                      {t('styleTest.edit')}
                    </button>
                    {prompt && (
                      <button
                        style={{ background: 'none', border: '1px solid var(--primary)', color: 'var(--primary)', borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                        onClick={() => navigator.clipboard.writeText(prompt)}
                      >
                        {t('styleTest.copy')}
                      </button>
                    )}
                    <button
                      style={{ background: 'none', border: '1px solid var(--text-3)', color: 'var(--text-3)', borderRadius: '4px', padding: '2px 8px', fontSize: '0.75rem', cursor: 'pointer' }}
                      onClick={handleReset}
                      title={t('styleTest.resetTitle')}
                    >
                      {t('styleTest.reset')}
                    </button>
                    <button
                      onClick={handleGenerate}
                      disabled={!canGenerate || generating}
                      style={{
                        background: canGenerate && !generating ? 'var(--success)' : 'var(--success-light)',
                        border: 'none', color: canGenerate && !generating ? 'white' : 'var(--success)',
                        borderRadius: '4px', padding: '2px 10px', fontSize: '0.75rem',
                        cursor: canGenerate && !generating ? 'pointer' : 'not-allowed',
                        display: 'flex', alignItems: 'center', gap: '4px',
                      }}
                    >
                      {generating && <span className="loading-spinner" style={{ width: '10px', height: '10px', borderWidth: '2px' }} />}
                      {generating ? t('styleTest.generatingText') : generatedImages.length > 0 ? t('styleTest.regenerate') : t('styleTest.generateBaseImage')}
                    </button>
                  </div>
                  {genError && (
                    <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.78rem', color: 'var(--danger)' }}>&#10060; {genError}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Candidate base images */}
          {generatedImages.length > 0 && (
            <div>
              <p style={{ margin: '0 0 0.3rem', fontSize: '0.75rem', color: 'var(--text-3)' }}>{t('styleTest.candidateImages')}</p>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {generatedImages.map((url, i) => (
                  <div
                    key={i}
                    aria-disabled={applyDisabled}
                    style={{ position: 'relative', flexShrink: 0, cursor: applyDisabled ? 'not-allowed' : 'pointer', opacity: applyDisabled ? 0.6 : 1 }}
                    onMouseEnter={() => setHoveredBaseIndex(i)}
                    onMouseLeave={() => setHoveredBaseIndex(null)}
                    onClick={() => { if (!applyDisabled) onApply(url); }}
                  >
                    <img
                      src={tosAccelerate(url)}
                      alt=""
                      style={{
                        width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', display: 'block',
                        border: '1px solid var(--border)',
                      }}
                    />
                    {hoveredBaseIndex === i && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setPreviewIndex(i); }}
                        style={{
                          position: 'absolute', bottom: '6px', left: '50%', transform: 'translateX(-50%)',
                          background: 'rgba(0,0,0,0.65)', color: 'white', border: 'none',
                          borderRadius: '4px', padding: '2px 8px', fontSize: '0.68rem',
                          cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        {t('styleTest.showLargeImage')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Candidate lightbox */}
      {previewIndex !== null && ReactDOM.createPortal(
        <SearchLightbox
          urls={generatedImages}
          index={previewIndex}
          onClose={() => setPreviewIndex(null)}
          onChange={setPreviewIndex}
        />,
        document.body
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        title={t('styleTest.deleteTitle')}
        message={t('styleTest.deleteMessage', { name: test.templateName })}
        onConfirm={() => { setConfirmDelete(false); onDelete(); }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ============================================================
// SearchLightbox
// ============================================================

const SearchLightbox: React.FC<{
  urls: string[];
  index: number;
  onClose: () => void;
  onChange: (i: number) => void;
  selectedUrls?: string[];
  onMultiSelect?: (url: string) => void;
  maxSelectable?: number;
}> = ({ urls, index, onClose, onChange, selectedUrls, onMultiSelect, maxSelectable }) => {
  const { t } = useTranslation('common');
  const prev = () => onChange((index - 1 + urls.length) % urls.length);
  const next = () => onChange((index + 1) % urls.length);
  const currentUrl = urls[index];
  const isMultiSelected = selectedUrls?.includes(currentUrl) ?? false;
  const multiSelectDisabled = !isMultiSelected && !!maxSelectable && (selectedUrls?.length ?? 0) >= maxSelectable;

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const res = await fetch(currentUrl);
      const blob = await res.blob();
      const ext = blob.type.split('/')[1] || 'jpg';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `image-${index + 1}.${ext}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      window.open(currentUrl, '_blank');
    }
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const btnStyle: React.CSSProperties = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
    fontSize: '2rem', width: '48px', height: '64px', cursor: 'pointer',
    borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    userSelect: 'none',
  };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {urls.length > 1 && (
        <button style={{ ...btnStyle, left: '16px' }} onClick={(e) => { e.stopPropagation(); prev(); }}>&#8249;</button>
      )}
      <img
        src={tosAccelerate(urls[index])}
        alt={`preview-${index + 1}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '88vw', maxHeight: '90vh', borderRadius: '8px', objectFit: 'contain' }}
      />
      {urls.length > 1 && (
        <button style={{ ...btnStyle, right: '16px' }} onClick={(e) => { e.stopPropagation(); next(); }}>&#8250;</button>
      )}
      <div style={{ position: 'absolute', bottom: '16px', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>{index + 1} / {urls.length}</span>
        <button
          onClick={handleDownload}
          style={{
            color: 'white', background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: '6px', padding: '4px 14px', fontSize: '0.82rem', cursor: 'pointer',
          }}
        >
          {t('styleTest.download')}
        </button>
        {onMultiSelect && (
          <button
            onClick={(e) => { e.stopPropagation(); if (!multiSelectDisabled) onMultiSelect(currentUrl); }}
            disabled={multiSelectDisabled}
            style={{
              color: 'white',
              background: isMultiSelected ? 'var(--primary)' : multiSelectDisabled ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.18)',
              border: isMultiSelected ? '1px solid var(--primary)' : '1px solid rgba(255,255,255,0.35)',
              borderRadius: '6px', padding: '4px 14px', fontSize: '0.82rem',
              cursor: multiSelectDisabled ? 'not-allowed' : 'pointer',
              opacity: multiSelectDisabled ? 0.5 : 1,
            }}
          >
            {isMultiSelected ? t('styleTest.selected') : multiSelectDisabled ? t('styleTest.limitReached') : t('styleTest.select')}
          </button>
        )}
      </div>
    </div>
  );
};
